import { useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { showModalPopup } from "../../../App/state/actions";
import moment from "moment";
import { numberStringCompareFunction } from "../../../shared/utils";
import constants from "../../../shared/constants";
import errorConstants from "../../../shared/constants/error";
import { channelCompareFunction, createEpgChannelObject } from "../../../shared/utils/epg";
const { MODAL_TYPES } = constants;
const { ERROR_TYPES } = errorConstants;

const useLiveSidebar = (
  isLivePlayer,
  getSegmentTimestamp,
  setMiniGuideOnNowPrograms,
  setMiniGuideOnNextPrograms,
  getNextSegmentTimestamp,
  loadGuidePrograms,
  liveProgramCanUpdate,
  isLookbackStream,
  lookbackContent,
  currentChannel,
  updateLookbackStream,
  setLiveProgramDetails,
  miniGuideOnNowPrograms,
  currentChannelId,
  userProfile,
  history,
  setLivePlaybackCanStart,
  subscribedChannels,
  appProvider,
  setCurrentIndex,
  restartUserInactivityTimer,
  triggerSafariUpdate,
  setTriggerSafariUpdate,
  channelMapInfo
) => {
  const currentPrograms = useSelector(({ epg }) => epg.currentPrograms);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const sidebarLastUpdateTimestamp = useRef(null);
  const isSidebarUpdated = useRef(false);

  // Concatenate current and future programs from redux store for use in mini guide
  const miniGuidePrograms = useMemo(() => {
    if (!isLivePlayer) {
      return null;
    }
    const currentSegmentTimestamp = getSegmentTimestamp();
    const segmentTimestamps = Object.keys(currentPrograms).sort(numberStringCompareFunction);
    return segmentTimestamps.reduce((result, timestamp) => {
      if (parseInt(timestamp, 10) < currentSegmentTimestamp) {
        // Ignore segments before the current segment
        return result;
      }

      const segmentPrograms = currentPrograms[timestamp] ?? {};
      for (const channelId in segmentPrograms) {
        if (!result[channelId]) {
          result[channelId] = [];
        }
        const channelPrograms = segmentPrograms[channelId];

        if (channelPrograms && Array.isArray(channelPrograms)) {
          channelPrograms.forEach((program) => {
            // Ignore duplicates
            if (result[channelId].some((previouslyAddedProgram) => previouslyAddedProgram.id === program.id)) {
              return;
            }
            result[channelId].push(program);
          });
        }
      }
      return result;
    }, {});
  }, [isLivePlayer, currentPrograms, getSegmentTimestamp]);

  const updateMiniGuidePrograms = useCallback(() => {
    if (!isLivePlayer || !miniGuidePrograms) {
      return;
    }
    const currentTimestamp = moment().valueOf();
    const onNowPrograms = {};
    const onNextPrograms = {};
    for (const channelId in miniGuidePrograms) {
      onNowPrograms[channelId] = miniGuidePrograms[channelId].find(
        (program) =>
          program.metadata.airingStartTime <= currentTimestamp && program.metadata.airingEndTime >= currentTimestamp
      );
      onNextPrograms[channelId] = miniGuidePrograms[channelId].find(
        (program) => program.metadata.airingStartTime === onNowPrograms[channelId]?.metadata.airingEndTime
      );
    }

    setMiniGuideOnNowPrograms(onNowPrograms);
    setMiniGuideOnNextPrograms(onNextPrograms);
  }, [miniGuidePrograms, isLivePlayer, setMiniGuideOnNowPrograms, setMiniGuideOnNextPrograms]);

  const updateSidePanelDetails = useCallback(
    (timestamp) => {
      if (timestamp) {
        if (sidebarLastUpdateTimestamp.current) {
          // Only update sidebar if it has been at least 1 minute since the last update
          const timeDiff = moment(timestamp).diff(sidebarLastUpdateTimestamp.current, "minutes");
          if (timeDiff >= 1) {
            sidebarLastUpdateTimestamp.current = timestamp;
            isSidebarUpdated.current = false;
          }
        } else {
          sidebarLastUpdateTimestamp.current = timestamp;
        }

        if (!isSidebarUpdated.current) {
          isSidebarUpdated.current = true;
          updateMiniGuidePrograms();
          const nextSegmentTimestamp = getNextSegmentTimestamp();
          if (!currentPrograms[nextSegmentTimestamp]) {
            loadGuidePrograms(nextSegmentTimestamp);
          }
        }
      }
    },
    [updateMiniGuidePrograms, getNextSegmentTimestamp, loadGuidePrograms, currentPrograms]
  );

  useEffect(() => {
    if (isLivePlayer && miniGuidePrograms) {
      updateMiniGuidePrograms();
    }
  }, [isLivePlayer, miniGuidePrograms, updateMiniGuidePrograms]);

  const updateLiveProgramDetails = useCallback(() => {
    // only update the program details when liveProgramCanUpdate is true
    if (isLookbackStream && lookbackContent.current && currentChannel) {
      !updateLookbackStream &&
        setLiveProgramDetails({
          ...lookbackContent.current,
          // TODO: refactor analytics to accept channel separately so we can remove it from liveProgramDetails
          channel: currentChannel,
        });
    } else if (miniGuideOnNowPrograms && currentChannel) {
      setLiveProgramDetails({
        ...miniGuideOnNowPrograms[currentChannelId],
        // TODO: refactor analytics to accept channel separately so we can remove it from liveProgramDetails
        channel: currentChannel,
      });
    }
  }, [
    currentChannelId,
    miniGuideOnNowPrograms,
    currentChannel,
    isLookbackStream,
    updateLookbackStream,
    setLiveProgramDetails,
    lookbackContent,
  ]);

  useEffect(() => {
    if (triggerSafariUpdate) {
      setTriggerSafariUpdate(false);
      updateLiveProgramDetails();
    }
  }, [triggerSafariUpdate, updateLiveProgramDetails, setTriggerSafariUpdate]);

  useEffect(() => {
    if (isLivePlayer && miniGuideOnNowPrograms) {
      updateLiveProgramDetails();
    }
  }, [isLivePlayer, miniGuideOnNowPrograms, updateLiveProgramDetails]);

  useEffect(() => {
    if (!miniGuideOnNowPrograms) return;
    if (isLivePlayer) {
      const currentLiveProgram = miniGuideOnNowPrograms[currentChannelId];
      if (currentLiveProgram?.metadata?.Entitlements?.includes("oh") && userProfile?.isLoggedIn) {
        dispatch(
          showModalPopup(MODAL_TYPES.ERROR, {
            title: ERROR_TYPES.ERROR,
            message: translate("message_in_home"),
            history,
          })
        );
        setLivePlaybackCanStart(false);
      }
    }
  }, [
    isLivePlayer,
    miniGuideOnNowPrograms,
    userProfile,
    currentChannelId,
    dispatch,
    translate,
    history,
    setLivePlaybackCanStart,
  ]);

  // List of subscribed user channels, sorted by channel number
  const miniGuideChannels = useMemo(() => {
    return (userProfile?.isLoggedIn ? subscribedChannels?.containers : channelMapInfo?.containers)
      ?.map((channel) => createEpgChannelObject(channel, appProvider.channelMapID))
      .sort(channelCompareFunction);
  }, [subscribedChannels, appProvider.channelMapID, channelMapInfo?.containers, userProfile?.isLoggedIn]);

  const onTabItemSelected = useCallback(
    (index) => {
      setCurrentIndex(index);
      restartUserInactivityTimer();
    },
    [setCurrentIndex, restartUserInactivityTimer]
  );

  return {
    miniGuidePrograms,
    updateSidePanelDetails,
    updateLiveProgramDetails,
    miniGuideChannels,
    onTabItemSelected,
  };
};

export default useLiveSidebar;
